import { Link } from '@tanstack/react-router';
import { Card, type AccentTone } from '@truecare/ui';
import type { MetricDefinitionResponse, MetricResult } from '@truecare/schemas';

/**
 * Phase 14a — single dashboard KPI card.
 *
 * Renders the metric's label + value with a status-coloured top accent rail.
 * When the catalog row carries a `drilldownPath`, the card becomes a link.
 */
export interface MetricCardProps {
  definition: MetricDefinitionResponse;
  result: MetricResult | undefined;
}

const STATUS_TONE: Record<MetricResult['status'], AccentTone> = {
  ok: 'forest',
  warning: 'sun',
  critical: 'danger',
};

// WCAG 1.4.1 — status must not be conveyed by the accent-rail colour alone.
// A text label + icon give a non-colour cue for the states that need action.
const STATUS_LABEL: Record<MetricResult['status'], string | null> = {
  ok: null,
  warning: 'Needs attention',
  critical: 'Critical',
};
const STATUS_ICON: Record<MetricResult['status'], string> = {
  ok: '',
  warning: '▲',
  critical: '⚠',
};

export function MetricCard({ definition, result }: MetricCardProps) {
  const status = result?.status ?? 'ok';
  // Phase 14b-3 audit C4 — `data-testid="metric-card-<id>"` on the outer
  // rendered element so Playwright Flow 5 (metric-card-drilldown) can locate
  // cards by prefix selector. Applied to the inner Card for the non-drilldown
  // branch + the Link wrapper for the drilldown branch.
  const testId = `metric-card-${definition.id}`;
  const isDrilldown = Boolean(definition.drilldownPath);
  const inner = (
    <Card accent="top" tone={STATUS_TONE[status]} padding="sm" className="h-full">
      <div className="text-xs uppercase tracking-wide text-ink-muted">
        {definition.label}
      </div>
      <div className="mt-2 font-heading text-2xl text-forest">
        {result === undefined ? '—' : String(result.value)}
      </div>
      {STATUS_LABEL[status] !== null && (
        <div
          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-ink"
          data-testid={`metric-card-status-${definition.id}`}
        >
          <span aria-hidden>{STATUS_ICON[status]}</span>
          <span>{STATUS_LABEL[status]}</span>
        </div>
      )}
      <p className="mt-2 text-xs text-ink-muted">{definition.description}</p>
      {/* Persistent drill-down affordance — only on cards that actually link
          somewhere. Cards with no `drilldownPath` carry no arrow so the cue
          matches reality (recognition over recall; touch users get no hover). */}
      {isDrilldown && (
        <div
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-forest"
          data-testid={`metric-card-drilldown-cue-${definition.id}`}
        >
          <span>View</span>
          <span aria-hidden>→</span>
        </div>
      )}
    </Card>
  );
  if (definition.drilldownPath) {
    // A drilldownPath may carry a query string (e.g. `/payouts?needsAttention=true`)
    // so the card lands on a pre-filtered view. TanStack's `to` is the pathname
    // only — split the query into a `search` object it can serialize.
    const [pathname, queryString] = definition.drilldownPath.split('?');
    // Coerce true/false to real booleans so TanStack serializes a clean
    // `?needsAttention=true` (a raw string value would round-trip as
    // `?needsAttention=%22true%22`). String flags stay strings.
    const search = queryString
      ? Object.fromEntries(
          [...new URLSearchParams(queryString)].map(([k, v]) => [
            k,
            v === 'true' ? true : v === 'false' ? false : v,
          ]),
        )
      : undefined;
    return (
      <Link
        to={pathname}
        search={search as never}
        className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-forest"
        data-testid={testId}
      >
        {inner}
      </Link>
    );
  }
  return <div data-testid={testId}>{inner}</div>;
}
